import { DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { WindowRefService } from '../../../../services/window-ref.service';
import { Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import { delayWhen, filter, publishReplay, refCount, tap } from 'rxjs/operators';

import { EventTrackingService } from '../../../../services/event-tracking.service';
import { DLEntry } from '../../../shared/models/description-list-entry.model';
import { INCOME_UPDATE_RES_DELAY } from '../../config/income.config';
import { CustomerProfileIncome, EmploymentStatus } from '../../models/income.model';
import { ProfileModalData } from '../../models/profile-details.model';
import {
  selectCustomerAttributesError,
  selectCustomerAttributesLoading,
  selectCustomerProfileIncome,
} from '../../../customer-attributes/store/customer-attributes.selector';

const employmentStatusFieldMapper: Record<EmploymentStatus, DLEntry<EmploymentStatus>> = {
  Unknown: { label: 'Unknown', value: 'Unknown' },
  SalariedEmployee: { label: 'Salaried Employee', value: 'SalariedEmployee' },
  HourlyWageEmployee: { label: 'Hourly Wage Employee', value: 'HourlyWageEmployee' },
  SelfEmployed: { label: 'Self Employed', value: 'SelfEmployed' },
  Contractor: { label: 'Contractor', value: 'Contractor' },
  Student: { label: 'Student', value: 'Student' },
  Retired: { label: 'Retired', value: 'Retired' },
  Unemployed: { label: 'Unemployed', value: 'Unemployed' },
  Other: { label: 'Other', value: 'Other' },
};

@Component({
    selector: 'app-profile-income',
    templateUrl: './profile-income.component.html',
    styleUrls: ['./profile-income.component.scss'],
    host: { class: 'flex-filled' },
    standalone: false
})
export class ProfileIncomeComponent implements OnInit {
  isLoading$ = this.store.select(selectCustomerAttributesLoading);
  error$ = this.store.select(selectCustomerAttributesError).pipe(publishReplay(1), refCount());
  isEditMode = false;
  employmentStatusOptions = Object.values(employmentStatusFieldMapper);
  isNullField: boolean;
  isSubmitting = false;
  incomeDataDlEntries: DLEntry[];
  incomeForm: UntypedFormGroup;
  modalData: ProfileModalData;
  incomeData$ = this.store.select(selectCustomerProfileIncome).pipe(
    filter((value) => !!value),
    delayWhen((incomeData) => {
      // when data is successfully updated, delay response to allow for success UI to render
      if (this.isEditMode && !incomeData.isCustomerIncomeAttributeRefreshRequired) {
        this.isSubmitting = true;
        return timer(INCOME_UPDATE_RES_DELAY);
      }
      return of(null);
    }),
    tap((incomeData) => {
      this.isNullField = this.getIsNullField(incomeData);

      this.isEditMode = this.isNullField;
      this.incomeDataDlEntries = this.getIncomeDlEntries(incomeData);
      this.incomeForm = this.buildForm(incomeData);
      this.modalData = {
        profileType: 'income',
        employmentStatus: employmentStatusFieldMapper[incomeData.customerProvidedEmploymentStatus],
        householdIncome: incomeData.customerProvidedHouseholdIncome?.toString(),
        individualGrossAnnualIncome: incomeData.customerProvidedIncome?.toString(),
      };
    })
  );
  constructor(
    private readonly decimalPipe: DecimalPipe,
    private readonly store: Store,

    private readonly windowRefService: WindowRefService,
    private readonly eventTrackingService: EventTrackingService
  ) {}

  buildForm(incomeData: CustomerProfileIncome) {
    return new UntypedFormGroup({
      individualGrossAnnualIncome: new UntypedFormControl(incomeData.customerProvidedIncome, [Validators.required]),
      householdIncome: new UntypedFormControl(incomeData.customerProvidedHouseholdIncome, [Validators.required]),
      employmentStatus: new UntypedFormControl(
        employmentStatusFieldMapper[incomeData.customerProvidedEmploymentStatus],
        [Validators.required]
      ),
    });
  }

  sendPageviewEvent() {
    const eventData = {
      eventName: 'pageView',
      // eventRepo: 'auth',
    };
    this.eventTrackingService.trackPageViewEvent(eventData);
  }

  ngOnInit(): void {
    this.sendPageviewEvent();
    this.windowRefService.nativeWindow.FS.event('[Customer Hub] Visit profile income update page');
  }

  toggleIsEditMode() {
    this.isEditMode = !this.isEditMode;

    if (!this.isEditMode) {
      // scroll back to top when switched out of edit mode
      window.scrollTo(0, 0);
    }
  }

  /**
   * Returns true if any of the displayed incomeData is null
   */
  getIsNullField(incomeData: CustomerProfileIncome): boolean {
    return (
      !incomeData.customerProvidedEmploymentStatus ||
      !incomeData.customerProvidedHouseholdIncome ||
      !incomeData.customerProvidedIncome
    );
  }

  getIncomeDlEntries(incomeData: CustomerProfileIncome) {
    return [
      {
        label: 'Individual Gross Annual Income',
        value: `${
          incomeData.customerProvidedIncome ? '$' + this.decimalPipe.transform(incomeData.customerProvidedIncome) : ''
        }`,
      },
      {
        label: 'Household Income',
        value: `${
          incomeData.customerProvidedHouseholdIncome
            ? '$' + this.decimalPipe.transform(incomeData.customerProvidedHouseholdIncome)
            : ''
        }`,
      },
      {
        label: 'Employment Status',
        value: employmentStatusFieldMapper[incomeData.customerProvidedEmploymentStatus]?.label || '',
      },
    ];
  }
}
